import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { FormattedTimeByDate } from '@10xLabs/react-web/components/FormattedTimeByDate'
import { Fragment, ReactElement } from 'react'
import { defineMessages, FormattedMessage, IntlShape, useIntl } from 'react-intl'
import styled from 'styled-components'

import { IPaymentSummaryTimelineEventModel, IPaymentSummaryTimelineEventName } from 'features/PaymentsSummary/types'
import Heading from 'components/v2/Heading'
import {
  EventLine,
  TimelineDateWrapper,
  TimelineDecoratorWrapper,
  TimelineDetailWrapper,
  TimelineGridWrapper
} from 'components/v2/Timeline'
import { FilePreview } from 'components/v2/InputFile'
import { paymentSummaryCategoryMessages } from 'features/PaymentsSummary/constants'
import { safeFormatMessage } from 'utils/intl'
import { isEmpty } from 'lodash'
import { SummarizeIcon } from 'components/Icons/SummarizeIcon'
import { DocumentIcon } from 'components/Icons/DocumentIcon'
import { EmailSentIcon } from 'components/Icons/EmailSentIcon'
import { getFileName } from 'utils/file'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TimelineDate = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #28265d;
  white-space: nowrap;
`

const TimelineTime = styled.div`
  font-size: 14px;
  color: #b0b0b0;
  font-weight: 500;
  white-space: nowrap;
`

const PaymentSummaryTimelineDetailWrapper = styled(TimelineDetailWrapper)`
  display: grid;
  gap: 16px;
`

const EventDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const EventName = styled.div`
  font-size: 16px;
  color: #28265d;
  font-weight: 500;
`

const EventAuthor = styled.div`
  font-size: 16px;
  color: #717171;
  font-weight: 400;
`

const eventNameMessages = defineMessages({
  [IPaymentSummaryTimelineEventName.EmailSent]: {
    id: 'paymentsSummary.timeline.eventName.emailSent',
    defaultMessage: 'Email Sent'
  },
  [IPaymentSummaryTimelineEventName.PaymentSummaryCreated]: {
    id: 'paymentsSummary.timeline.eventName.paymentSummaryCreated',
    defaultMessage: 'Payment Summary Created'
  }
})

const getEventName = (event: IPaymentSummaryTimelineEventModel, intl: IntlShape): string => {
  if (paymentSummaryCategoryMessages[event.name as keyof typeof paymentSummaryCategoryMessages]) {
    return safeFormatMessage({ intl, messages: paymentSummaryCategoryMessages, key: event.name })
  } else {
    return safeFormatMessage({ intl, messages: eventNameMessages, key: event.name })
  }
}

const getEventIcon = (event: IPaymentSummaryTimelineEventModel): ReactElement | null => {
  if (!isEmpty(event.action.files)) {
    return <DocumentIcon />
  }

  switch (event.name) {
    case IPaymentSummaryTimelineEventName.EmailSent:
      return <EmailSentIcon />
    case IPaymentSummaryTimelineEventName.PaymentSummaryCreated:
      return <SummarizeIcon />
    default:
      return null
  }
}

interface IPaymentSummaryTimelineProps {
  timeline: IPaymentSummaryTimelineEventModel[]
}

export const PaymentSummaryTimeline = ({ timeline }: IPaymentSummaryTimelineProps): ReactElement => {
  const intl = useIntl()

  return (
    <Wrapper>
      <Heading.H5>
        <FormattedMessage id="paymentsSummary.timeline.title" defaultMessage="Timeline" />
      </Heading.H5>
      <TimelineGridWrapper>
        {[...timeline].reverse().map((event, index) => {
          const isLastEvent = index === timeline.length - 1
          const shouldShowLine = !isLastEvent
          const icon = getEventIcon(event)
          const eventName = getEventName(event, intl)

          return (
            <Fragment key={event.id}>
              <TimelineDateWrapper>
                <div>
                  <TimelineDate>
                    <FormattedDate value={event.createdAt} year="numeric" month="short" day="2-digit" />
                  </TimelineDate>
                  <TimelineTime>
                    <FormattedTimeByDate date={event.createdAt} intl={intl} />
                  </TimelineTime>
                </div>
              </TimelineDateWrapper>
              <TimelineDecoratorWrapper>
                <EventLine color="#5d59d7" showLine={shouldShowLine} icon={icon} />
              </TimelineDecoratorWrapper>
              <PaymentSummaryTimelineDetailWrapper>
                <EventDescription>
                  <EventName>{eventName}</EventName>
                  <EventAuthor>
                    <FormattedMessage
                      id="paymentsSummary.timeline.eventAuthor"
                      defaultMessage="by {firstName} {lastName}"
                      values={{ firstName: event.user.firstName, lastName: event.user.lastName }}
                    />
                  </EventAuthor>
                </EventDescription>
                {event.action.files.map((file) => {
                  return (
                    <FilePreview key={file.id} file={file.path as string} filename={getFileName(file.path as string)} />
                  )
                })}
              </PaymentSummaryTimelineDetailWrapper>
            </Fragment>
          )
        })}
      </TimelineGridWrapper>
    </Wrapper>
  )
}
